import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { FirebaseApp, initializeApp } from 'firebase/app'
import { getMessaging, getToken, Messaging } from 'firebase/messaging'
import { NotificationsLanguageModal } from '../../../i18n'
import { notificationSettingsPushToken } from '../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { RESTMethods } from '../../NonReact/constants/REST'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../ReactRouting/Hooks/useUserInfo'
import { StatusCodes } from '../../NonReact/constants/StatusCodes'
import { requestJSON } from '../../NonReact/common/API/REST/RESTClient'
import { IconName } from '../Common/Components/Icon'
import IconButton, { IconButtonType } from '../Common/Components/IconButton'
import { LanguageContext, MessageContext, Page } from '../Common/Contexts'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { UserSettings, useUserSettings } from '../Common/Hooks/useUserSettings'
import { ReactState } from '../Common/Types/Types'
import { $error } from '../Common/Utils/ConsoleUtils'
import {
    FIREBASE_VAPIDKEY,
    firebaseConfig,
    NotificationChannels,
    NotificationIconTypes,
    NotificationPermissions,
    NotificationPushServiceTypes
} from './Notifications/Constants'
import NotificationsModal from './Notifications/NotificationsModal'
import { NotificationIcons, NotificationIconsWithID, NotificationTokenRequest } from './Notifications/Types'
import { ApplicationResponseNotification, JSONResponse, RESTOptions } from '../../NonReact/common/Types/Types'
import { getPageName } from '../../NonReact/common/DataIngest'

type Props = {
    disableIcon?: boolean
    isOpenState?: ReactState<boolean>
}

export const NotificationIcon: FC<Props> = ({ disableIcon, isOpenState }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(NotificationsLanguageModal.NAME)
    const { displayError }: MessageContextType = useContext(MessageContext)

    // eslint-disable-next-line
    const [firebaseObj, setFirebaseObj]: ReactState<Nullable<Messaging>> = useState<Nullable<Messaging>>(null)
    const [firebaseServiceWorker, setFirebaseServiceWorker]: ReactState<Nullable<ServiceWorkerRegistration>> =
        useState<Nullable<ServiceWorkerRegistration>>(null)
    const [isOpen, setIsOpen]: ReactState<boolean> = isOpenState ?? useState<boolean>(false)
    const [isIconGrayedOut, setIsIconGrayedOut]: ReactState<boolean> = useState<boolean>(true)

    const [userInfo]: UserInfo = useUserInfo()
    const [userSettings, setUserSettings]: UserSettings = useUserSettings()

    const [displayNotification, setDisplayNotification]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)

    const currentPageName: string = getPageName()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const handleFirebaseTokenRequest = async (
        notificationSettings: ApplicationResponseNotification,
        messaging: Nullable<Messaging>,
        serviceWorker?: Nullable<ServiceWorkerRegistration>
    ): Promise<void> => {
        if (
            isVarNotEmpty(messaging) &&
            !notificationSettings.DoNotDisturb &&
            notificationSettings.NotificationChannels.includes(NotificationChannels.BROWSER_PUSH)
        ) {
            const permission: NotificationPermission = await Notification.requestPermission()
            if (permission !== NotificationPermissions.GRANTED) return

            if (IsVarEmpty(serviceWorker)) return

            navigator.serviceWorker.ready.then((): void => {
                getToken(messaging, {
                    vapidKey: FIREBASE_VAPIDKEY,
                    serviceWorkerRegistration: serviceWorker
                })
                    .then((currentToken: string): void => {
                        if (currentToken) {
                            requestJSON(notificationSettingsPushToken, {
                                method: RESTMethods.PUT,
                                body: {
                                    Token: currentToken,
                                    PushService: NotificationPushServiceTypes.FCM
                                },
                                errorOnNoContent: false,
                                additionalAllowableNoContentCodes: [StatusCodes.NOT_MODIFIED_CLIENT_REDIRECTION_CODE]
                            } as RESTOptions<NotificationTokenRequest>).catch(displayError)
                        }
                    })
                    .catch(displayError)
            })
        }
    }

    const handleNotificationFetchRequest = (
        response: JSONResponse<ApplicationResponseNotification>,
        messaging: Nullable<Messaging>,
        serviceWorker?: Nullable<ServiceWorkerRegistration>
    ): void => {
        setUserSettings(response ?? undefined)

        const isNotificationsSetup: boolean = response?.NotificationsTelSysID === null

        if (!isNotificationsSetup && response) handleFirebaseTokenRequest(response, messaging, serviceWorker)

        if (isNotificationsSetup) return setDisplayNotification(NotificationIconTypes.ADD)

        return response?.DoNotDisturb
            ? setDisplayNotification(NotificationIconTypes.DISABLE)
            : setDisplayNotification(NotificationIconTypes.ENABLE)
    }

    const handleRefreshNotifications = (updatedNotifications: ApplicationResponseNotification): void => {
        handleFirebaseTokenRequest(updatedNotifications, firebaseObj, firebaseServiceWorker)
        handleNotificationFetchRequest(updatedNotifications, firebaseObj, firebaseServiceWorker)
    }

    const registerReady = (scriptURL: string, options?: RegistrationOptions): Promise<ServiceWorkerRegistration> =>
        navigator.serviceWorker
            .register(scriptURL, options)
            .then((registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> => {
                // If there is an active worker and nothing incoming, we are done.
                const incomingSw: Nullable<ServiceWorker> = registration.installing || registration.waiting
                if (registration.active && !incomingSw) return Promise.resolve(registration)

                // If not, wait for the newest service worker to become activated.
                return new Promise<ServiceWorkerRegistration>(
                    (
                        resolve: (value: ServiceWorkerRegistration | PromiseLike<ServiceWorkerRegistration>) => void,
                        reject: (reason?: Error) => void
                    ): void => {
                        if (incomingSw) {
                            incomingSw.onstatechange = (evt: Event): void => {
                                if ((evt.target as ServiceWorker)?.state === 'activated') {
                                    incomingSw.onstatechange = null
                                    setFirebaseServiceWorker(registration)
                                    resolve(registration)
                                }
                            }
                        } else {
                            reject(new Error('No incoming service worker found.'))
                        }
                    }
                )
            })
            .catch((err: Error): Promise<never> => {
                $error('Error registering service worker:', err)
                return Promise.reject(err)
            })

    useEffect((): void => {
        if (currentPageName !== Page.WELCOME) {
            if (userInfo?.Scope?.IsDeviceTypeGroup) {
                setIsIconGrayedOut(false)
            } else {
                setIsIconGrayedOut(true)
            }
        }
    }, [userInfo])

    useEffect((): void => {
        if (currentPageName === Page.WELCOME || location.href.includes('localhost')) return

        if (location.origin.includes('localhost')) return

        registerReady('/firebase-messaging-sw.js', {
            scope: '/'
        }).then((registration: ServiceWorkerRegistration): void => {
            if (!isMounted.current) return

            try {
                const app: FirebaseApp = initializeApp(firebaseConfig)
                const messaging: Messaging = getMessaging(app)

                setFirebaseObj(messaging)

                if (isVarNotEmpty(userSettings)) {
                    handleNotificationFetchRequest(userSettings, messaging, registration)
                }
            } catch (error) {
                $error(error)
            }
        })
    }, [currentPageName, userSettings])

    const handleNotificationClick = (): void => setIsOpen(true)

    const notificationIcons: NotificationIconsWithID[] = [
        {
            id: NotificationIconTypes.ADD,
            iconName: IconName.NOTIFICATION_ADD,
            title: language.get(NotificationsLanguageModal.IconAddTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        },
        {
            id: NotificationIconTypes.ENABLE,
            iconName: IconName.NOTIFICATION_ENABLED,
            title: language.get(NotificationsLanguageModal.IconEnabledTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        },
        {
            id: NotificationIconTypes.DISABLE,
            iconName: IconName.NOTIFICATION_DISABLED,
            title: language.get(NotificationsLanguageModal.IconDisabledTooltip),
            type: IconButtonType.BUTTON,
            onClick: (): void => handleNotificationClick()
        }
    ]

    const getNotificationIconProps = (): ReactElement => {
        if (displayNotification === null) return <></>

        const filteredNotification: NotificationIconsWithID = notificationIcons.find(
            (icon) => icon.id === displayNotification
        ) as NotificationIconsWithID

        const props: NotificationIcons = {
            iconName: filteredNotification.iconName,
            title: isIconGrayedOut ? EMPTY_STRING : filteredNotification.title,
            type: filteredNotification.type,
            onClick: filteredNotification.onClick
        } as NotificationIcons

        return (
            <IconButton
                disabledSubTitle={language.get(NotificationsLanguageModal.IconDisabled)}
                isEnabled={!isIconGrayedOut}
                {...props}
            />
        )
    }

    return isVarNotEmpty(firebaseObj) || location.href.includes('localhost') ? (
        <>
            {disableIcon ? <></> : getNotificationIconProps()}
            {isVarNotEmpty(userSettings) ? (
                <NotificationsModal
                    NotificationSettings={userSettings}
                    refreshNotifications={handleRefreshNotifications}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                />
            ) : (
                <></>
            )}
        </>
    ) : (
        <></>
    )
}
